import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Sort } from '@angular/material/sort';
import { SupplierService } from '../../supplier.service';
import { SupplierApiKeyService, SupplierApiKeyModel } from '../../supplier-api-key.service';
import { SupplierApiKeyCreateComponent } from '../../modals/supplier-api-key-create/supplier-api-key-create.component';
import { DataTableBase } from '../../../../../../core/base/components/data-table-base/data-table-base';
import { FeiColumn } from '../../../../../../core/base/interfaces/fei-column.interface';
import { PermissionMessageService } from '../../../../../../core/services/permission-message.service';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';

@Component({
  selector: 'vex-supplier-details-api',
  templateUrl: './supplier-details-api.component.html',
  styleUrls: ['./supplier-details-api.component.scss'],
})
export class SupplierDetailsApiComponent extends DataTableBase<SupplierApiKeyModel> implements OnInit, OnDestroy {
  supplierLabel: string;
  supplierId: string;
  loadingData = false;
  dataSource = new MatTableDataSource<SupplierApiKeyModel>([]);
  override sortField = 'createdAt';
  override sortOrder = 'desc';

  feisColumns: Array<FeiColumn> = [
    { label: 'Libellé', column: 'label', propertyDisplay: 'label' },
    { label: 'Identifiant', column: 'technicalIdentifier', propertyDisplay: 'technicalIdentifier' },
    { label: 'Dernière utilisation', column: 'lastUsedAt', type: 'date' },
    { label: 'Créée le', column: 'createdAt', type: 'date' },
    { label: 'Statut', column: 'statut', type: 'custom' },
    {
      label: '',
      column: 'actions',
      canUpdate: false,
      canDelete: false,
      canSuspend: false,
      showAction: true,
    },
  ];

  constructor(
    private readonly supplierService: SupplierService,
    private readonly supplierApiKeyService: SupplierApiKeyService,
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly permissionMessageService: PermissionMessageService,
  ) {
    super();
  }

  ngOnInit(): void {
    const sub = this.supplierService.entity$.subscribe(supplier => {
      if (supplier?._id) {
        this.supplierId = supplier._id;
        this.supplierLabel = supplier.label;
        this.loadApiKeys();
      }
    });
    this.subscription.add(sub);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  loadApiKeys(): void {
    this.loadingData = true;
    this.supplierApiKeyService.findAll(this.supplierId, this.sortField, this.sortOrder).subscribe({
      next: res => {
        this.dataSource.data = res.data ?? (res as any);
        this.loadingData = false;
      },
      error: () => {
        this.loadingData = false;
      },
    });
  }

  onSortEvent(event: Sort): void {
    const column = this.feisColumns.find(x => x.column === event.active);
    this.sortField = column?.propertyDisplay || event.active;
    this.sortOrder = event.direction;
    this.loadApiKeys();
  }

  openCreateModal(): void {
    this.dialog
      .open(SupplierApiKeyCreateComponent, {
        data: { supplierId: this.supplierId },
      })
      .afterClosed()
      .subscribe(created => {
        if (created) {
          this.loadApiKeys();
        }
      });
  }

  revokeKey(key: SupplierApiKeyModel): void {
    // Filet impératif : le bouton est rendu par la datatable partagée, la directive permissionBlock le masque déjà.
    if (this.permissionMessageService.blockIfMissingPermission(ENUM_PERMISSIONS.MANAGE_SUPPLIER_API_KEY)) {
      return;
    }
    this.supplierApiKeyService.revokeApiKey(this.supplierId, key._id).subscribe({
      next: () => {
        this.snackBar.open('Clé révoquée avec succès', 'Fermer', { duration: 3000 });
        this.loadApiKeys();
      },
      error: () => {
        this.snackBar.open('Erreur lors de la révocation', 'Fermer', { duration: 3000 });
      },
    });
  }

  deleteItem(key: SupplierApiKeyModel): void {
    this.supplierApiKeyService.deleteApiKey(this.supplierId, key._id).subscribe({
      next: () => {
        this.snackBar.open('Clé supprimée', 'Fermer', { duration: 3000 });
        this.loadApiKeys();
      },
      error: () => {
        this.snackBar.open('Erreur lors de la suppression', 'Fermer', { duration: 3000 });
      },
    });
  }

  isRevoked(key: SupplierApiKeyModel): boolean {
    return !!key?.revokedAt;
  }
}
